/**
 * UI 音效库（基于 Mochi-UI-Sounds，Web Audio API 浏览器端合成，无需音频文件）。
 * 通过 localStorage 的音效开关统一控制，App 与网页共用。
 */

const SOUND_ENABLED_KEY = 'anshangSoundEnabled';

let _ctx: AudioContext | null = null;
let _volume = 0.4; // 全局音量 50%（用户要求）
let _unlocked = false;

export function isSoundEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SOUND_ENABLED_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled: boolean) {
  try {
    window.localStorage.setItem(SOUND_ENABLED_KEY, enabled ? '1' : '0');
  } catch {
    // 忽略隐私模式等存储失败
  }
}

export function setSoundVolume(volume: number) {
  _volume = Math.min(1, Math.max(0, volume));
}

function getCtx(): AudioContext {
  if (typeof window === 'undefined') {
    throw new Error('AudioContext only available in the browser');
  }
  if (!_ctx) {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    _ctx = new Ctor();
  }
  if (_ctx.state === 'suspended') void _ctx.resume();
  return _ctx;
}

export function unlockSound() {
  if (typeof window === 'undefined') return;
  if (_unlocked && _ctx?.state === 'running') return;
  try {
    const ctx = getCtx();
    const source = ctx.createBufferSource();
    source.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    source.connect(ctx.destination);
    source.start(0);
    _unlocked = true;
    if (ctx.state === 'suspended') void ctx.resume();
  } catch {
    // 无音频环境时静默降级
  }
}

type Wave = OscillatorType;

function tone(opts: {
  freq: number;
  duration: number;
  type?: Wave;
  gain?: number;
  attack?: number;
  release?: number;
  glideTo?: number;
  delay?: number;
}) {
  if (_volume <= 0) return;
  const ctx = getCtx();
  const t0 = ctx.currentTime + (opts.delay ?? 0);
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = opts.type ?? 'sine';
  osc.frequency.setValueAtTime(opts.freq, t0);
  if (opts.glideTo) {
    osc.frequency.exponentialRampToValueAtTime(opts.glideTo, t0 + opts.duration);
  }
  const peak = (opts.gain ?? 0.18) * _volume;
  const a = Math.min(opts.attack ?? 0.005, opts.duration * 0.4);
  const r = Math.min(opts.release ?? 0.08, opts.duration - a);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + a);
  g.gain.linearRampToValueAtTime(peak, t0 + opts.duration - r);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.duration);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + opts.duration + 0.02);
}

function noiseBurst(duration: number, gain = 0.08, lp = 2000) {
  if (_volume <= 0) return;
  const ctx = getCtx();
  const t0 = ctx.currentTime;
  const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * duration), ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = lp;
  const g = ctx.createGain();
  g.gain.setValueAtTime(gain * _volume, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  src.connect(filter).connect(g).connect(ctx.destination);
  src.start(t0);
  src.stop(t0 + duration);
}

const soundLibrary = {
  /** 通用点击 */
  click: () => tone({ freq: 880, duration: 0.06, type: 'triangle', gain: 0.2 }),

  /** 轻快气泡音：选中任务、选择选项 */
  pop: () => tone({ freq: 520, glideTo: 880, duration: 0.12, type: 'sine', gain: 0.22 }),

  /** 开关打开 */
  toggleOn: () => {
    tone({ freq: 660, duration: 0.08, type: 'triangle', gain: 0.18 });
    tone({ freq: 990, duration: 0.1, type: 'triangle', gain: 0.18, delay: 0.06 });
  },

  /** 开关关闭 */
  toggleOff: () => {
    tone({ freq: 660, duration: 0.08, type: 'triangle', gain: 0.18 });
    tone({ freq: 440, duration: 0.1, type: 'triangle', gain: 0.18, delay: 0.06 });
  },

  /** 成功完成：专注完成 */
  success: () => {
    tone({ freq: 523.25, duration: 0.12, type: 'triangle', gain: 0.18 }); // C5
    tone({ freq: 659.25, duration: 0.12, type: 'triangle', gain: 0.18, delay: 0.1 }); // E5
    tone({ freq: 783.99, duration: 0.2, type: 'triangle', gain: 0.2, delay: 0.2 }); // G5
  },

  /** 升级庆祝：完成一组复习 */
  levelUp: () => {
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((f, i) =>
      tone({ freq: f, duration: 0.14, type: 'triangle', gain: 0.2, delay: i * 0.09 }),
    );
  },

  /** 开始动作：计时开始 */
  open: () => {
    tone({ freq: 440, glideTo: 880, duration: 0.18, type: 'sine', gain: 0.18 });
    noiseBurst(0.12, 0.05, 3000);
  },

  /** 结束/暂停动作 */
  close: () => tone({ freq: 880, glideTo: 330, duration: 0.18, type: 'sine', gain: 0.18 }),

  /** 勾选待办 */
  check: () => {
    tone({ freq: 660, duration: 0.06, type: 'triangle', gain: 0.12 });
    tone({ freq: 990, duration: 0.1, type: 'triangle', gain: 0.12, delay: 0.045 });
  },

  /** 取消勾选 */
  uncheck: () => tone({ freq: 780, glideTo: 420, duration: 0.12, type: 'triangle', gain: 0.11 }),

  /** 翻卡/切换 */
  snap: () => {
    tone({ freq: 880, duration: 0.035, type: 'triangle', gain: 0.12 });
    tone({ freq: 1320, duration: 0.06, type: 'sine', gain: 0.08, delay: 0.025 });
  },

  /** 模块切换：导航栏跳页。低-高快速滑音 + 短白噪音尾，参考 mochi-ui-sounds 的 nav 风格 */
  nav: () => {
    tone({ freq: 440, glideTo: 880, duration: 0.16, type: 'triangle', gain: 0.18 });
    noiseBurst(0.08, 0.04, 2400);
  },

  /** 通知提示 */
  notify: () => {
    tone({ freq: 880, duration: 0.18, type: 'sine', gain: 0.2 });
    tone({ freq: 1318.5, duration: 0.22, type: 'sine', gain: 0.18, delay: 0.12 });
  },

  /** 悠远钟声：一组复习全部完成 */
  chime: () => {
    [1046.5, 1318.5, 1568].forEach((f, i) =>
      tone({ freq: f, duration: 0.6, type: 'sine', gain: 0.12, release: 0.5, delay: i * 0.04 }),
    );
  },

  /** 删除/丢弃 */
  drop: () => tone({ freq: 880, glideTo: 220, duration: 0.22, type: 'triangle', gain: 0.2 }),
} as const;

export type SoundName = keyof typeof soundLibrary;

/** 播放音效：受设置里的音效开关控制，任何异常都静默忽略。 */
export function playSound(name: SoundName) {
  if (!isSoundEnabled()) return;
  try {
    unlockSound();
    soundLibrary[name]();
  } catch {
    // 无音频环境（如测试）时静默降级
  }
}
